import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Movie } from "@/types";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [keyword, setKeyword] = useState("");
  const slides = movies.slice(0, 5);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  if (slides.length === 0) return null;
  const current = slides[active];

  return (
    <section className="relative w-full">
      <div className="relative h-[440px] w-full overflow-hidden md:h-[560px]">
        {slides.map((movie, index) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === active ? "opacity-100" : "opacity-0"}`}
          >
            <img
              src={movie.still}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} 香港三级片电影剧照`}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/65 to-primary-950/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/30" />

        <div className="relative z-10 mx-auto flex h-full w-full flex-col justify-center px-4 md:px-6">
          <div className="max-w-2xl animate-fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-accent-950">
              <i className="ri-fire-line" />
              焦点推荐 · {current.year}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <p className="mt-2 text-sm text-background-200/90 md:text-base">
              {current.titleEn} · {current.director} 导演 · {current.genre.join(" / ")} · {current.runtime} 分钟
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">{current.synopsis}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-400"
              >
                查看详情
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to={`/category/${current.decade}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-folder-line" />
                浏览同期影片
              </Link>
            </div>
          </div>

          <form
            onSubmit={handleSearch}
            className="mt-8 flex w-full max-w-xl items-center overflow-hidden rounded-full border border-background-200/30 bg-background-50/15 backdrop-blur"
          >
            <span className="flex h-12 w-12 items-center justify-center text-background-100">
              <i className="ri-search-2-line text-lg" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              name="hero_search"
              placeholder="输入片名、导演或关键词，快速检索电影档案"
              className="h-12 flex-1 bg-transparent pr-2 text-sm text-background-50 outline-none placeholder:text-background-100/70"
            />
            <button
              type="submit"
              className="m-1 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
            >
              搜索
            </button>
          </form>
        </div>

        <button
          type="button"
          aria-label="上一部"
          onClick={() => setActive((prev) => (prev - 1 + slides.length) % slides.length)}
          className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => setActive((prev) => (prev + 1) % slides.length)}
          className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 right-5 z-10 flex items-center gap-2">
          {slides.map((movie, index) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => setActive(index)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                index === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}